import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { Branch, StaffMember } from "@dentacore/shared-types";

import { listBranches } from "../api/branches";
import { usePhoneCountry } from "../api/use-phone-country";
import {
  addMembership,
  listStaff,
  reissueInvite,
  removeMembership,
  updateStaff,
} from "../api/staff";
import { formatPhone } from "../ui/countries";
import { useAuth } from "../auth/auth-context";
import { useCan } from "../auth/use-can";
import { useModules } from "../shell/modules-context";
import { useBranchTimezone } from "../api/use-branch-timezone";
import { today } from "../home/home-data";
import { PaneHead } from "./pane";
import { PersonAccess } from "./person-access";
import p from "../plans/procedures.module.css";
import a from "./access.module.css";
import styles from "./settings.module.css";
import { Toast } from "./toast";
import { describe, useWriter } from "./use-writer";
import type { Writer } from "./use-writer";
import { PhoneInput } from "../ui/phone-input";
import { RequiredMark } from "../ui/required-mark";

/**
 * One person: their role, where they may work, and whether they still have
 * access — **level two of the People drill-down** (A1).
 *
 * Three rules the server owns and this screen only reports:
 *
 * - **The last admin cannot be demoted or deactivated** (A3). Refused with a
 *   sentence; the control is offered anyway, because guessing at it here would
 *   be a second implementation of a rule that lives in the service.
 * - **Revoking a location warns with the count and proceeds** (A4) — not the
 *   refusal that retiring a chair gets, because the appointments keep their
 *   provider and stay visible.
 * - **Deactivation bites within fifteen minutes, not instantly** (A2), and this
 *   screen says so rather than implying otherwise.
 */
type PersonTab = "access" | "locations" | "contact";

const PERSON_TABS: readonly { key: PersonTab; label: string }[] = [
  { key: "access", label: "Access" },
  { key: "locations", label: "Locations" },
  { key: "contact", label: "Contact" },
];

export function PersonPage(): React.JSX.Element {
  const { userId } = useParams<{ userId: string }>();
  const { busy, toast, dismiss, fail, run } = useWriter();
  const { user } = useAuth();
  const hasChatbot = useModules().has("CHATBOT");
  // Setting anyone's assistant number is access (AU-D1).
  const setsAssistant = useCan()("access.assign");

  const [person, setPerson] = useState<StaffMember | null | "missing">(null);
  const [branches, setBranches] = useState<readonly Branch[]>([]);
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);
  const [tab, setTab] = useState<PersonTab>("access");

  const reload = useCallback(
    async (signal?: AbortSignal) => {
      const rows = await listStaff(signal);
      setPerson(rows.find((row) => row.id === userId) ?? "missing");
    },
    [userId],
  );

  useEffect(() => {
    const controller = new AbortController();

    Promise.all([
      reload(controller.signal),
      listBranches(controller.signal).then(setBranches),
    ]).catch((cause: unknown) => {
      if (controller.signal.aborted) return;
      setPerson("missing");
      fail(describe(cause, "Could not read this person."));
    });

    return () => controller.abort();
  }, [reload, fail]);

  if (person === "missing") {
    return (
      <div className={styles.paneInner}>
        <BackLink />
        <p className={styles.blank}>That person is not in this clinic.</p>
        {toast !== null && <Toast kind={toast.kind} message={toast.message} onDismiss={dismiss} />}
      </div>
    );
  }

  if (person === null) {
    return (
      <div className={styles.paneInner}>
        <BackLink />
        <p className={styles.quiet}>Reading&hellip;</p>
      </div>
    );
  }

  const held = new Set(person.memberships.map((m) => m.branchId));
  const elsewhere = branches.filter((branch) => !held.has(branch.id));

  return (
    <div className={styles.personPage}>
      <BackLink />

      {/*
        **`PaneHead`, like every other pane.** This carried its own `.title` at
        28px while the eight rail panes use `.paneTitle` at 24px, so a person's
        name was the largest heading anywhere in Settings — larger than the
        screen it sits inside.
      */}
      <PaneHead title={`${person.firstName} ${person.lastName}`} testId="person-title" />

      <header className={styles.head}>
        {!person.isActive && (
          <p className={styles.notice} data-testid="person-no-access">
            This account has no access. That is either someone who has left, or someone who has not
            opened their invite link yet.
          </p>
        )}
      </header>

      {/*
        **Three tabs, opening on Access** (MS-D12, Emile 2026-10-16). Two columns
        filled the width but still ran 200px past a 1366×768 window; one tab at a
        time fits it, and every card is still here.
      */}
      <div className={a.tabs} role="tablist" aria-label="About this person">
        {PERSON_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            className={tab === t.key ? `${a.tab} ${a.tabOn}` : a.tab}
            onClick={() => setTab(t.key)}
            data-testid={`person-tab-${t.key}`}
          >
            {t.label}
            {t.key === "locations" && (
              <span className={a.tabCount}>{person.memberships.length}</span>
            )}
          </button>
        ))}
      </div>

      <div role="tabpanel" hidden={tab !== "access"} className={styles.personPanel}>
        {/*
            ── access: Administrator, roles, exceptions (STORY-168) ──────────────

            **Administrator stays separate from the job** (2026-09-07) — the owner
            of a one-dentist practice is DENTIST and an administrator. The job card
            lives in PersonAccess too since 2026-10-04, because a new job changes
            access wherever nobody gave them a role (R-D6a) and the dialog that
            changes it says so before Save.
          */}
        <PersonAccess
          userId={person.id}
          isSelf={person.id === user?.id}
          // Read again when anything on this page changes what they can do —
          // their locations, and whether they can sign in at all, which
          // Sign-in changes right above the "Deactivated" notice — so the card
          // never shows yesterday's access (QA).
          version={`${person.memberships.map((m) => m.branchId).join(",")}|${String(person.isActive)}`}
          onChanged={() => reload()}
          // **Sign-in lives here, beside the "Deactivated" notice** (tester,
          // 2026-10-19): it sat under Contact in a second card called Access,
          // so the tab that said "until they are reactivated" had no way to.
          signIn={<SignInCard person={person} busy={busy} run={run} reload={reload} />}
          // Roles are given per location (R-D6), and locations are added on
          // the Locations tab — so the roles list points there.
          onAddLocation={elsewhere.length > 0 ? () => setTab("locations") : undefined}
        />
      </div>

      <div role="tabpanel" hidden={tab !== "locations"} className={styles.personPanel}>
        {/* ── where they may work ───────────────────────────────────────────── */}
        <section className={`${styles.card} ${styles.cardPlain}`}>
          <h3 className={styles.cardTitle}>Locations</h3>

          {person.memberships.length === 0 ? (
            <p className={styles.blank} data-testid="person-no-locations">
              No locations yet, so they cannot open the calendar or the patient file anywhere.
            </p>
          ) : (
            <ul className={styles.list}>
              {person.memberships.map((membership) => {
                const ahead = membership.futureAppointmentCount ?? 0;
                const confirming = confirmRevoke === membership.branchId;

                return (
                  <li
                    key={membership.branchId}
                    className={styles.row}
                    data-testid="person-location"
                  >
                    <div className={styles.rowBody}>
                      <span className={styles.rowName}>{membership.branchName}</span>
                      {ahead > 0 && (
                        <span className={styles.rowMeta} data-testid="person-location-ahead">
                          {ahead} appointment{ahead === 1 ? "" : "s"} still to come
                        </span>
                      )}
                    </div>

                    <div className={styles.rowActions}>
                      {confirming ? (
                        <div className={styles.confirm}>
                          <span className={styles.confirmText} data-testid="revoke-confirm">
                            {ahead > 0
                              ? `They are the provider on ${String(ahead)} appointment${
                                  ahead === 1 ? "" : "s"
                                } still to come here. Those keep their time and their provider — but nobody will be able to work them from this account. Take the location away?`
                              : "Take this location away?"}
                          </span>
                          <div className={styles.confirmActions}>
                            <button
                              type="button"
                              className={styles.danger}
                              disabled={busy}
                              onClick={() => {
                                run(
                                  () => removeMembership(person.id, membership.branchId),
                                  reload,
                                  "Access taken away.",
                                );
                                setConfirmRevoke(null);
                              }}
                              data-testid="revoke-yes"
                            >
                              Take it away
                            </button>
                            <button
                              type="button"
                              className={styles.ghost}
                              disabled={busy}
                              onClick={() => setConfirmRevoke(null)}
                            >
                              Keep it
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className={styles.ghost}
                          disabled={busy}
                          onClick={() => setConfirmRevoke(membership.branchId)}
                          data-testid="revoke-open"
                        >
                          Take away
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {elsewhere.length > 0 && (
            <div className={styles.inlineForm}>
              <select
                className={styles.input}
                defaultValue=""
                disabled={busy}
                aria-label="Give access to a location"
                onChange={(event) => {
                  const branchId = event.target.value;
                  if (branchId === "") return;
                  event.target.value = "";
                  run(() => addMembership(person.id, branchId), reload, "Access given.");
                }}
                data-testid="person-add-location"
              >
                <option value="">Give access to&hellip;</option>
                {elsewhere.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </section>
      </div>

      <div role="tabpanel" hidden={tab !== "contact"} className={styles.personContact}>
        {/*
            ── contact ──────────────────────────────────────────────────────────
            EB9 — read-only. Both were captured when the person was added and
            shown nowhere after. Admin-only like the rest of System: the API strips
            them for anyone else, so this reaches nobody who could not already
            manage the account.
          */}
        <section className={`${styles.card} ${styles.cardPlain}`} data-testid="person-contact">
          <h3 className={styles.cardTitle}>Contact</h3>
          <dl className={styles.facts}>
            <div className={styles.fact}>
              <dt className={styles.label}>Email</dt>
              <dd className={styles.factValue} data-testid="person-email">
                {person.email}
              </dd>
            </div>
            <div className={styles.fact}>
              <dt className={styles.label}>Phone</dt>
              <dd className={styles.factValue} data-testid="person-phone">
                {person.phoneE164 === null ? "Not set" : formatPhone(person.phoneE164)}
              </dd>
            </div>
          </dl>
        </section>

        {/*
            ── the assistant number (STORY-120) ──────────────────────────────────

            **Set here, by an admin — not by the person themselves.** The design
            this table was first proposed under had them link their own number
            from a session of their own; the client chose this instead after the
            cost was named a second time (docs/DECISIONS.md, *STORY-120 grill*).
            Recorded as an accepted risk, not hidden behind a friendlier label.
          */}
        {/* The staff assistant is Chatbot's (MOD-D6): hidden without it (QF-D9). */}
        {hasChatbot && (
          <section className={`${styles.card} ${styles.cardPlain}`}>
            <h3 className={styles.cardTitle}>WhatsApp assistant</h3>
            {!setsAssistant ? (
              <dl className={styles.facts}>
                <div className={styles.fact}>
                  <dt className={styles.label}>Assistant number</dt>
                  <dd className={styles.factValue} data-testid="person-assistant-phone-read">
                    {person.assistantPhoneE164 === null
                      ? "Not set"
                      : formatPhone(person.assistantPhoneE164)}
                  </dd>
                </div>
              </dl>
            ) : (
              <AssistantPhoneField
                initial={person.assistantPhoneE164}
                busy={busy}
                onSave={(assistantPhoneE164) =>
                  run(
                    () => updateStaff(person.id, { assistantPhoneE164 }),
                    reload,
                    (result) => {
                      if (assistantPhoneE164 === null) return "Assistant access removed.";
                      const collision = (result as StaffMember).patientCollision;
                      return collision == null
                        ? "Assistant number set."
                        : `Assistant number set. Note: this number is also ${collision.patientName}'s ` +
                            `(file ${collision.fileNumberDisplay}) — worth checking.`;
                    },
                  )
                }
              />
            )}
          </section>
        )}

        {/* ── their own Google calendar ─────────────────────────────────────── */}
        {CLINICAL_ROLES.has(person.role) && (
          <section className={`${styles.card} ${styles.cardPlain}`}>
            <h3 className={styles.cardTitle}>Their Google calendar</h3>

            <GoogleAddressField
              initial={person.googleCalendarEmail}
              busy={busy}
              onSave={(email) =>
                run(
                  () => updateStaff(person.id, { googleCalendarEmail: email }),
                  reload,
                  email === null
                    ? "Their calendar has been removed."
                    : "Their calendar is being set up.",
                )
              }
            />
          </section>
        )}
      </div>

      {toast !== null && <Toast kind={toast.kind} message={toast.message} onDismiss={dismiss} />}
    </div>
  );
}

function BackLink(): React.JSX.Element {
  return (
    <Link to="/people/staff" className={styles.back} data-testid="person-back">
      &lsaquo; Staff
    </Link>
  );
}

/**
 * Whether they can sign in at all: a new invite link and *Restore access* for
 * someone without it, *Take access away* for someone with it. **Called Sign-in,
 * not Access**, because the card it sits beside already is (tester, 2026-10-19).
 */
function SignInCard({
  person,
  busy,
  run,
  reload,
}: {
  person: StaffMember;
  busy: boolean;
  run: Writer["run"];
  reload: () => Promise<void>;
}): React.JSX.Element {
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  // Their last day, asked with the deactivation so their salary stops (QF-D7).
  const timezone = useBranchTimezone();
  const [lastDay, setLastDay] = useState(() => today(timezone));
  const [link, setLink] = useState<string | null>(null);
  // Set when a new invite went by email only, and whether it went (QF-D24).
  const [emailedOnly, setEmailedOnly] = useState<{ emailed: boolean } | null>(null);

  return (
    <section className={`${styles.card} ${styles.cardPlain}`} data-testid="person-sign-in">
      <h3 className={styles.cardTitle}>Sign-in</h3>

      {!person.isActive ? (
        <>
          {/*
            The new link, or word of the email, takes the status's place on the
            row rather than a line of its own, which ran 1366×768 off the
            bottom (MS-D12). **Only someone who can change access sees an
            invite link** (QF-D24): whoever holds it can choose the password.
          */}
          <div className={`${a.adminRow} ${a.signInRow}`}>
            {link !== null ? (
              <input
                className={`${styles.input} ${a.linkField}`}
                readOnly
                value={link}
                onFocus={(event) => event.currentTarget.select()}
                aria-label="Invite link"
                data-testid="person-invite-link"
              />
            ) : emailedOnly !== null ? (
              <span className={a.muted} data-testid="person-invite-emailed">
                {emailedOnly.emailed
                  ? "We have emailed them a new link to set their password."
                  : "The email did not go out. Ask an administrator to send a new invite."}
              </span>
            ) : (
              <span className={a.muted}>Cannot sign in</span>
            )}
            <span className={a.signInActions}>
              <button
                type="button"
                className={`${p.primary} ${a.small}`}
                disabled={busy}
                onClick={() =>
                  run(
                    async () => {
                      const issued = await reissueInvite(person.id);
                      // **The server's link, verbatim** (QA20-5). Built here it
                      // was `window.location.origin`, which is the apex whenever
                      // an admin runs their clinic from it — as D3 lets them —
                      // and a link to the apex is refused by the accept page.
                      setLink(issued.acceptUrl);
                      setEmailedOnly(
                        issued.acceptUrl === null ? { emailed: issued.emailed } : null,
                      );
                    },
                    reload,
                    "New link made. The old one no longer works.",
                  )
                }
                data-testid="person-reissue"
              >
                Make a new link
              </button>
              <button
                type="button"
                className={`${p.ghost} ${a.small}`}
                disabled={busy}
                onClick={() =>
                  run(() => updateStaff(person.id, { isActive: true }), reload, "Access restored.")
                }
                data-testid="person-reactivate"
              >
                Restore access
              </button>
            </span>
          </div>
        </>
      ) : confirmDeactivate ? (
        <div className={styles.confirm}>
          <span className={styles.confirmText} data-testid="deactivate-confirm">
            {person.firstName} will not be able to sign in. Everything they recorded stays.
          </span>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="deactivate-last-day">
              Last day
              <RequiredMark />
            </label>
            <input
              id="deactivate-last-day"
              type="date"
              className={styles.input}
              value={lastDay}
              max={today(timezone)}
              disabled={busy}
              onChange={(event) => setLastDay(event.target.value)}
              data-testid="deactivate-last-day"
            />
          </div>
          <div className={styles.confirmActions}>
            <button
              type="button"
              className={styles.danger}
              disabled={busy || lastDay === ""}
              onClick={() => {
                run(
                  () => updateStaff(person.id, { isActive: false, lastDay }),
                  reload,
                  "Access taken away. Any open session stops within fifteen minutes.",
                );
                setConfirmDeactivate(false);
              }}
              data-testid="deactivate-yes"
            >
              Take access away
            </button>
            <button
              type="button"
              className={styles.ghost}
              disabled={busy}
              onClick={() => setConfirmDeactivate(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className={a.adminRow}>
          <span className={a.muted}>Can sign in</span>
          <button
            type="button"
            className={styles.danger}
            disabled={busy}
            onClick={() => setConfirmDeactivate(true)}
            data-testid="person-deactivate"
          >
            Take access away
          </button>
        </div>
      )}
    </section>
  );
}

/**
 * The address a clinician's own calendar is shared with (decision G22).
 *
 * **Saved on a button rather than on every keystroke**, unlike the role select
 * beside it: each save creates or deletes a Google calendar, and doing that per
 * character would make a dozen calendars while somebody typed their address.
 *
 * Clearing it and saving takes the calendar away, which the placeholder says —
 * an empty box that silently does nothing would be the more confusing choice.
 */
function GoogleAddressField({
  initial,
  busy,
  onSave,
}: {
  initial: string | null;
  busy: boolean;
  onSave: (email: string | null) => void;
}): React.JSX.Element {
  const [value, setValue] = useState(initial ?? "");

  // Re-seeded when the person is re-read, so a save elsewhere is reflected here.
  useEffect(() => setValue(initial ?? ""), [initial]);

  const trimmed = value.trim();
  const changed = trimmed !== (initial ?? "");

  return (
    <div className={styles.googleRow}>
      <input
        className={styles.input}
        type="email"
        value={value}
        disabled={busy}
        placeholder="their.name@gmail.com"
        aria-label="Their Google address"
        onChange={(event) => setValue(event.target.value)}
        data-testid="person-google-email"
      />
      <button
        type="button"
        className={styles.primary}
        disabled={busy || !changed}
        onClick={() => onSave(trimmed === "" ? null : trimmed)}
        data-testid="person-google-save"
      >
        {trimmed === "" && initial !== null ? "Remove it" : "Save"}
      </button>
    </div>
  );
}

/**
 * Who is offered a calendar — the glossary's providers, matching the set the
 * server pairs. A receptionist is never a booking's provider, so theirs would
 * always be empty.
 */
const CLINICAL_ROLES = new Set(["DENTIST", "HYGIENIST"]);

/**
 * STORY-120, following `GoogleAddressField`'s exact shape: local state seeded
 * from the person, a save button disabled until something changed, saving
 * `null` to clear rather than a separate control — "remove someone's
 * assistant access" has to be as easy as setting it was.
 */
function AssistantPhoneField({
  initial,
  busy,
  onSave,
}: {
  initial: string | null;
  busy: boolean;
  onSave: (assistantPhoneE164: string | null) => void;
}): React.JSX.Element {
  const [value, setValue] = useState(initial ?? "");
  const phoneCountry = usePhoneCountry();

  // Re-seeded when the person is re-read, so a save elsewhere is reflected here.
  useEffect(() => setValue(initial ?? ""), [initial]);

  const trimmed = value.trim();
  const changed = trimmed !== (initial ?? "");

  return (
    <div className={styles.googleRow}>
      <PhoneInput
        value={value}
        onChange={setValue}
        defaultCountry={phoneCountry}
        testIdPrefix="person-assistant-phone"
        label="Assistant number"
      />
      <button
        type="button"
        className={styles.primary}
        disabled={busy || !changed}
        onClick={() => onSave(trimmed === "" ? null : trimmed)}
        data-testid="person-assistant-phone-save"
      >
        {trimmed === "" && initial !== null ? "Remove it" : "Save"}
      </button>
    </div>
  );
}
